import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import { DialogClose } from '../../../components/dialogs/DialogFrame/DialogClose';
import {
  CompanyShareSelection,
  ISharePayload,
  IShareTarget,
  ISharedMessageType,
} from '../../../dto/MessageSharing.dto';
import {
  useRqShareMessage,
  useRqUnshareMessage,
} from '../../../react_query/messages/sharing';
import Toast from '../../../package/Toaster';
import { buildShareTargets, hydrateShareSelections } from './buildShareTargets';
import MessageVisibilityEditor from './MessageVisibilityEditor';
import { useDistrictMessageSharingAccess } from './useDistrictMessageSharingAccess';

interface ShareManagementDialogProps {
  open: boolean;
  handleClose: () => void;
  messageType: ISharedMessageType;
  requestId: number;
  companyId: number;
  existingTargets?: IShareTarget[];
  onTargetsChange?: (targets: IShareTarget[]) => void;
}

const ShareManagementDialog: React.FC<ShareManagementDialogProps> = ({
  open,
  handleClose,
  messageType,
  requestId,
  companyId,
  existingTargets = [],
  onTargetsChange,
}) => {
  const {canManageDistrictSharing} = useDistrictMessageSharingAccess(companyId);
  const [selections, setSelections] = useState<CompanyShareSelection[]>([]);
  const [hasHydratedSelections, setHasHydratedSelections] = useState(false);

  const shareMutation = useRqShareMessage(companyId, messageType, requestId);
  const unshareMutation = useRqUnshareMessage(
    companyId,
    messageType,
    requestId,
  );

  useEffect(() => {
    if (!open) {
      setSelections([]);
      setHasHydratedSelections(false);
      return;
    }

    setHasHydratedSelections(false);
    setSelections(hydrateShareSelections(existingTargets));
    setHasHydratedSelections(true);
  }, [existingTargets, open]);

  const targets = useMemo(() => buildShareTargets(selections), [selections]);
  const hasExistingTargets = existingTargets.length > 0;
  const isSaving = shareMutation.isLoading || unshareMutation.isLoading;
  const canSave = hasHydratedSelections;

  const handleRemoveSharing = useCallback(() => {
    unshareMutation.mutate(undefined, {
      onSuccess: () => {
        onTargetsChange?.([]);
        Toast('Message sharing removed', 'success');
        handleClose();
      },
    });
  }, [handleClose, onTargetsChange, unshareMutation]);

  const handleSave = useCallback(() => {
    if (targets.length === 0) {
      if (!hasExistingTargets) {
        // No targets selected and nothing to remove — close the dialog
        // so the user gets feedback that their "don't share" choice was
        // acknowledged (matches the behavior of saving with selections).
        handleClose();
        return;
      }

      handleRemoveSharing();
      return;
    }

    const payload: ISharePayload = {
      is_active: true,
      targets,
    };

    shareMutation.mutate(payload, {
      onSuccess: response => {
        onTargetsChange?.(response.targets);
        Toast('Message shared successfully', 'success');
        handleClose();
      },
    });
  }, [
    handleClose,
    handleRemoveSharing,
    hasExistingTargets,
    onTargetsChange,
    shareMutation,
    targets,
  ]);

  if (!canManageDistrictSharing) return null;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='lg'>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        px={3}
        pt={2}
        pb={1}
        sx={{borderBottom: '1px solid gray'}}>
        <Typography variant='h6'>Share Message</Typography>
        <DialogClose onClose={handleClose} />
      </Stack>

      <DialogContent sx={{minHeight: 400}}>
        <MessageVisibilityEditor
          companyId={companyId}
          value={selections}
          onChange={setSelections}
          autoSelectOnEmpty={false}
        />
      </DialogContent>

      <DialogActions sx={{px: 3, pb: 2}}>
        {existingTargets.length > 0 && (
          <Button
            variant='outlined'
            color='error'
            onClick={handleRemoveSharing}
            disabled={isSaving}
            sx={{mr: 'auto'}}>
            {unshareMutation.isLoading ? 'Removing...' : 'Remove Sharing'}
          </Button>
        )}
        <Button variant='outlined' onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant='contained'
          disabled={!canSave || isSaving}
          onClick={handleSave}>
          {isSaving ? <CircularProgress size={20} color='inherit' /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareManagementDialog;
